import { bindEvents, offset } from '../../assets/js/helpers';

import App from '../../assets/js/app';

import AnimationController from '../../assets/js/controller/animation';
import AudioController from '../../assets/js/controller/audio';
import CursorController from '../../assets/js/controller/cursor';


const selector = 'quote';

export default class Quote {

	constructor(section) {
		this.el = {
			section,
			quote: section.querySelector('q'),
			highlight: null,
			words: null,
			toggleFallback: section.closest('.wrapper-sticky-side').querySelector('.narrator__toggle-fallback')
		};

		this.threshold = 0;
		this.offset = 0;

		this.interval = null;

		this.duration = 0;
		this.currentTime = 0;
		this.progressLength = 0;
		this.ended = false;

		this.wordTime = [];

		this.animation = null;

		this.audioId = `quote-${this.el.section.dataset.audioContent}`;
		this.audio = AudioController.add(this.audioId, this.el.section.dataset.audio);

		this.events = bindEvents(this);

		this.addEventListeners();
		this.wrapText();
		this.measure();
	}

	static get selector() {
		return selector;
	}

	wrapText() {
		this.el.highlight = document.createElement('div');
		const words = this.el.quote.innerText.split(' ');
		const total = this.el.quote.innerText.length - words.length - 1;
		let time = 0;
		for (const w of words) {
			time += w.length / total;
			this.wordTime.push(time);
		}
		this.el.highlight.innerHTML = `<span>${words.join('</span> <span>')}</span>`;
		this.el.quote.appendChild(this.el.highlight);
		this.el.words = this.el.quote.querySelectorAll('span');
	}

	measure() {
		this.offset = offset.top(this.el.quote);
		this.threshold = window.innerHeight * 0.5;
	}

	update() {
		if (this.audio.currentTime !== this.currentTime) {
			this.currentTime = this.audio.currentTime;
			CursorController.updateProgress(this.currentTime / this.audio.duration);
			return true;
		}
	}

	render() {
		for (let i = 0, l = this.el.words.length; i < l; ++i) {
			this.el.words[i].style.opacity = 1;
			if (this.wordTime[i] * this.audio.duration > this.audio.currentTime) break;
		}
	}

	startAnimation() {
		this.animation = AnimationController.set({
			update: this.update.bind(this),
			render: this.render.bind(this)
		}, 'quote');
	}

	pauseAnimation() {
		AnimationController.clear(this.animation);
	}

	reset() {
		for (const w of this.el.words) w.style.opacity = '';
	}

	_onWindowLoad() {
		this.measure();
	}

	_onWindowResize() {
		this.measure();
	}

	_onAudioPlay() {
		if (this.ended) this.reset();
		this.startAnimation();
		AudioController.volume(this.audioId, 1);
		AudioController.volume('background', 0.2);
		this.el.toggleFallback.classList.add('is-playing');
	}

	_onAudioPause() {
		this.pauseAnimation();
		AudioController.volume('background', 1);
		this.el.toggleFallback.classList.remove('is-playing');
	}

	_onAudioEnded() {
		this.ended = true;
		this.pauseAnimation();
		AudioController.volume('background', 1);
		this.el.toggleFallback.classList.remove('is-playing');
		CursorController.cancelHijack('pause');
	}

	_onQuoteReveal() {
		if (!App.muted) {
			this.audio.play()
				.catch(() => {
					this.el.toggleFallback.classList.add('is-visible');
				});
		}
	}

	_onQuoteMouseEnter() {
		CursorController.startHijack(this.audio.paused ? 'play' : 'pause');
	}

	_onQuoteMouseLeave() {
		CursorController.cancelHijack('play');
		CursorController.cancelHijack('pause');
	}

	_onQuoteClick(e) {
		e.preventDefault();
		if (this.audio.paused) {
			this.audio.play();
			CursorController.cancelHijack('play');
			CursorController.startHijack('pause');
		} else {
			this.pauseAnimation();
			CursorController.cancelHijack('pause');
			CursorController.startHijack('play');
			AudioController.volume('background', 1);
			AudioController.volume(this.audioId, 0).then(() => {
				this.audio.pause();
			});
			this.el.toggleFallback.classList.remove('is-playing');
		}
	}

	_onToggleFallbackClick(e) {
		e.preventDefault();
		if (this.audio.paused) {
			this.audio.play();
		} else {
			this.pauseAnimation();
			AudioController.volume('background', 1);
			AudioController.volume(this.audioId, 0).then(() => {
				this.audio.pause();
			});
			this.el.toggleFallback.classList.remove('is-playing');
		}
	}

	addEventListeners() {
		window.addEventListener('load', this.events._onWindowLoad);
		window.addEventListener('resize', this.events._onWindowResize);

		this.audio.addEventListener('play', this.events._onAudioPlay);
		this.audio.addEventListener('pause', this.events._onAudioPause);
		this.audio.addEventListener('ended', this.events._onAudioEnded);

		this.el.quote.addEventListener('reveal', this.events._onQuoteReveal);
		this.el.quote.addEventListener('mouseenter', this.events._onQuoteMouseEnter);
		this.el.quote.addEventListener('mouseleave', this.events._onQuoteMouseLeave);
		this.el.quote.addEventListener('click', this.events._onQuoteClick);
		this.el.toggleFallback.addEventListener('click', this.events._onToggleFallbackClick);
	}

	removeEventListeners() {
		window.removeEventListener('load', this.events._onWindowLoad);
		window.removeEventListener('resize', this.events._onWindowResize);

		this.audio.removeEventListener('play', this.events._onAudioPlay);
		this.audio.removeEventListener('pause', this.events._onAudioPause);
		this.audio.removeEventListener('ended', this.events._onAudioEnded);

		this.el.quote.removeEventListener('reveal', this.events._onQuoteReveal);
		this.el.quote.removeEventListener('mouseenter', this.events._onQuoteMouseEnter);
		this.el.quote.removeEventListener('mouseleave', this.events._onQuoteMouseLeave);
		this.el.quote.removeEventListener('click', this.events._onQuoteClick);
		this.el.toggleFallback.removeEventListener('click', this.events._onToggleFallbackClick);
	}

	destroy() {
		AudioController.volume('background', 1);
		AudioController.volume(this.audioId, 0).then(() => {
			AudioController.remove(this.audioId);
		});
		this.pauseAnimation();
		this.removeEventListeners();
	}

}
